/**
 * @fileoverview The browser's zone, offered once per authenticated load.
 *
 * An instructor who never chose a timezone and never wrote a schedule slot
 * used to be framed in UTC — silently. The browser knows where they are, so
 * on an authenticated load this offers its zone to the server, which stores
 * it only when the account has none (decision D3). When a zone is already
 * stored and the browser disagrees, nothing is rewritten: the instructor is
 * asked once per detected zone, and a "keep" is remembered in this browser,
 * because an instructor on tour must not have their home zone silently moved.
 *
 * Renders nothing. Mounted inside the instructor auth provider so it sees the
 * same `user` the shell renders from.
 */

import { useEffect, useRef } from "react";
import { toast } from "sonner";

import api from "@/lib/api";

/** The IANA zone this browser is in, or "" when it will not say. */
export function detectZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

const keyFor = (zone) => `hans:timezone-hint:${zone}`;

function wasKept(zone) {
  try {
    return localStorage.getItem(keyFor(zone)) === "1";
  } catch {
    return false;
  }
}

function remember(zone) {
  try {
    localStorage.setItem(keyFor(zone), "1");
  } catch {
    /* A private window forgets; the question is asked again next time. */
  }
}

const readable = (zone) => String(zone || "").replaceAll("_", " ");

export default function TimezoneHint({ user, setUser }) {
  const asked = useRef(false);

  useEffect(() => {
    if (!user || asked.current) return;
    const detected = detectZone();
    if (!detected) return;
    asked.current = true;

    if (!user.timezone) {
      api
        .post("/auth/timezone-hint", { timezone: detected })
        .then((res) => {
          if (!res.data?.stored) return;
          setUser((current) =>
            current ? { ...current, timezone: res.data.timezone, timezone_source: res.data.source } : current,
          );
        })
        .catch(() => {
          /* Nothing to do: the next load offers it again. */
        });
      return;
    }

    if (detected === user.timezone || wasKept(detected)) return;

    toast(`You look like you're in ${readable(detected)} — update your timezone?`, {
      id: "timezone-hint",
      duration: 20000,
      action: {
        label: "Update",
        onClick: () => {
          api
            .put("/auth/profile", { timezone: detected })
            .then((res) => {
              setUser(res.data);
              toast.success(`Times now shown in ${readable(detected)}.`);
            })
            .catch(() => toast.error("Couldn't update your timezone. Try again from Settings."));
        },
      },
      cancel: {
        label: `Keep ${readable(user.timezone)}`,
        onClick: () => remember(detected),
      },
      onDismiss: () => remember(detected),
    });
  }, [user, setUser]);

  return null;
}
